import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ConfigProvider } from "antd";
import arEG from "antd/locale/ar_EG";
import deDE from "antd/locale/de_DE";
import enUS from "antd/locale/en_US";
import esES from "antd/locale/es_ES";
import frFR from "antd/locale/fr_FR";
import hiIN from "antd/locale/hi_IN";
import jaJP from "antd/locale/ja_JP";
import knIN from "antd/locale/kn_IN";
import mlIN from "antd/locale/ml_IN";
import ruRU from "antd/locale/ru_RU";
import taIN from "antd/locale/ta_IN";
import zhCN from "antd/locale/zh_CN";
import i18n from "../i18n";

export const languages = [
  { code: "en", label: "English" },
  { code: "ta", label: "Tamil" },
  { code: "hi", label: "Hindi" },
  { code: "ml", label: "Malayalam" },
  { code: "te", label: "Telugu" },
  { code: "kn", label: "Kannada" },
  { code: "ar", label: "Arabic" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "es", label: "Spanish" },
  { code: "zh-CN", label: "Chinese (Simplified)" },
  { code: "ja", label: "Japanese" },
  { code: "ru", label: "Russian" },
];

const antdLocales = {
  en: enUS,
  ta: taIN,
  hi: hiIN,
  ml: mlIN,
  kn: knIN,
  ar: arEG,
  fr: frFR,
  de: deDE,
  es: esES,
  "zh-CN": zhCN,
  ja: jaJP,
  ru: ruRU,
};

const LANGUAGE_STORAGE_KEY = "appLanguage";
const I18NEXT_STORAGE_KEY = "i18nextLng";
const GOOGLE_TRANSLATE_COOKIE = "googtrans";
const GOOGLE_TRANSLATE_ELEMENT_ID = "google_translate_element";
const GOOGLE_TRANSLATE_SCRIPT_ID = "google-translate-script";
const fallbackLanguage = "en";
const LanguageContext = createContext(null);

const normalizeLanguage = (languageCode) =>
  languages.some((language) => language.code === languageCode)
    ? languageCode
    : fallbackLanguage;

const getInitialLanguage = () => {
  return normalizeLanguage(
    localStorage.getItem(LANGUAGE_STORAGE_KEY) ||
      localStorage.getItem(I18NEXT_STORAGE_KEY) ||
      i18n.resolvedLanguage ||
      i18n.language
  );
};

const getCookieDomains = () => {
  const domains = [window.location.hostname];
  const hostParts = window.location.hostname.split(".");
  if (hostParts.length > 1) {
    domains.push(`.${hostParts.slice(-2).join(".")}`);
  }
  return domains.filter(Boolean);
};

const clearGoogleTranslateState = () => {
  const expires = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie = `${GOOGLE_TRANSLATE_COOKIE}=;path=/;${expires}`;
  document.cookie = `${GOOGLE_TRANSLATE_COOKIE}=;path=/;domain=;${expires}`;
  getCookieDomains().forEach((domain) => {
    document.cookie = `${GOOGLE_TRANSLATE_COOKIE}=;path=/;domain=${domain};${expires}`;
  });
};

const persistGoogleTranslateCookie = (languageCode) => {
  if (languageCode === fallbackLanguage) {
    clearGoogleTranslateState();
    return;
  }
  document.cookie = `${GOOGLE_TRANSLATE_COOKIE}=/en/${languageCode};path=/`;
  document.cookie = `${GOOGLE_TRANSLATE_COOKIE}=/auto/${languageCode};path=/`;
};

const ensureGoogleTranslateElement = () => {
  let element = document.getElementById(GOOGLE_TRANSLATE_ELEMENT_ID);
  if (!element) {
    element = document.createElement("div");
    element.id = GOOGLE_TRANSLATE_ELEMENT_ID;
    element.style.display = "none";
    document.body.appendChild(element);
  }
  return element;
};

const getMultilingualValue = (value, languageCode, fallbackCode = fallbackLanguage) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;

  const directValue =
    value[languageCode] ||
    value[languageCode?.replace("-", "_")] ||
    value[languageCode?.split("-")[0]] ||
    value[`${languageCode}_IN`] ||
    value[`${languageCode}-IN`];
  if (directValue) return directValue;

  return value[fallbackCode] || value.en || value.default || value.value || "";
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(getInitialLanguage);
  const [isReady, setIsReady] = useState(i18n.isInitialized);
  const translateTimerRef = useRef(null);
  const observerRef = useRef(null);

  const syncGoogleTranslate = useCallback((languageCode) => {
    const nextLanguage = normalizeLanguage(languageCode);
    persistGoogleTranslateCookie(nextLanguage);

    if (translateTimerRef.current) {
      window.clearInterval(translateTimerRef.current);
    }

    let attempts = 0;
    translateTimerRef.current = window.setInterval(() => {
      const combo = document.querySelector(".goog-te-combo");
      attempts += 1;

      if (combo) {
        const comboValue = nextLanguage === fallbackLanguage ? "" : nextLanguage;
        if (combo.value !== comboValue) {
          combo.value = comboValue;
          combo.dispatchEvent(new Event("change"));
        }
        window.clearInterval(translateTimerRef.current);
        translateTimerRef.current = null;
      } else if (attempts >= 40) {
        window.clearInterval(translateTimerRef.current);
        translateTimerRef.current = null;
      }
    }, 200);
  }, []);

  const translateText = useCallback(
    (value, options) => {
      if (value === null || value === undefined) return "";
      if (typeof value === "object") {
        return getMultilingualValue(value, language);
      }
      return i18n.exists(String(value)) ? i18n.t(String(value), options) : String(value);
    },
    [language]
  );

  const changeLanguage = useCallback(async (languageCode) => {
    const nextLanguage = normalizeLanguage(languageCode);

    localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
    localStorage.setItem(I18NEXT_STORAGE_KEY, nextLanguage);
    if (i18n.language !== nextLanguage) {
      await i18n.changeLanguage(nextLanguage);
    }
    setLanguage(nextLanguage);
    syncGoogleTranslate(nextLanguage);
  }, [syncGoogleTranslate]);

  useEffect(() => {
    changeLanguage(getInitialLanguage());
  }, [changeLanguage]);

  useEffect(() => {
    if (i18n.isInitialized) {
      setIsReady(true);
      return undefined;
    }

    const handleInitialized = () => setIsReady(true);
    i18n.on("initialized", handleInitialized);
    return () => i18n.off("initialized", handleInitialized);
  }, []);

  useEffect(() => {
    ensureGoogleTranslateElement();

    window.googleTranslateElementInit = () => {
      if (!window.google?.translate?.TranslateElement) return;
      new window.google.translate.TranslateElement(
        {
          pageLanguage: fallbackLanguage,
          includedLanguages: languages
            .filter((item) => item.code !== fallbackLanguage)
            .map((item) => item.code)
            .join(","),
          autoDisplay: false,
        },
        GOOGLE_TRANSLATE_ELEMENT_ID
      );
      syncGoogleTranslate(getInitialLanguage());
    };

    if (!document.getElementById(GOOGLE_TRANSLATE_SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = GOOGLE_TRANSLATE_SCRIPT_ID;
      script.src =
        "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.async = true;
      document.body.appendChild(script);
    } else if (window.google?.translate?.TranslateElement) {
      window.googleTranslateElementInit();
    }

    observerRef.current = new MutationObserver(() => {
      if (language !== fallbackLanguage) {
        syncGoogleTranslate(language);
      }
    });
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      if (translateTimerRef.current) {
        window.clearInterval(translateTimerRef.current);
      }
      observerRef.current?.disconnect();
    };
  }, [language, syncGoogleTranslate]);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
  }, [language]);

  useEffect(() => {
    const handleLanguageChanged = (nextLanguage) => {
      if (nextLanguage && nextLanguage !== language) {
        setLanguage(nextLanguage);
      }
    };

    const handleStorage = (event) => {
      if (
        (event.key === LANGUAGE_STORAGE_KEY || event.key === I18NEXT_STORAGE_KEY) &&
        event.newValue
      ) {
        changeLanguage(event.newValue);
      }
    };

    i18n.on("languageChanged", handleLanguageChanged);
    window.addEventListener("storage", handleStorage);
    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
      window.removeEventListener("storage", handleStorage);
    };
  }, [changeLanguage, language]);

  const selectedLanguage = useMemo(
    () => languages.find((item) => item.code === language) || languages[0],
    [language]
  );
  const selectedAntdLocale = useMemo(
    () => antdLocales[language] || antdLocales[language?.split("-")[0]] || enUS,
    [language]
  );

  const value = useMemo(
    () => ({
      language,
      selectedLanguage,
      languages,
      changeLanguage,
      translateText,
    }),
    [changeLanguage, language, selectedLanguage, translateText]
  );

  return (
    <LanguageContext.Provider value={value}>
      <ConfigProvider locale={selectedAntdLocale} direction={language === "ar" ? "rtl" : "ltr"}>
        {isReady ? children : null}
      </ConfigProvider>
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used inside LanguageProvider");
  }
  return context;
}
